import React from 'react';
import { Search, MessageCircle, Phone, Mail, HelpCircle, Book } from 'lucide-react';

function HelpCenter() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient rounded-lg z-10"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?auto=format&fit=crop&q=80&w=1920&h=1080"
              alt="Help center hero"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-5xl font-bold text-center text-white mb-6">Help Center</h1>
            <p className="text-xl text-white/80 text-center max-w-3xl mx-auto">
              Find answers, get support, and resolve issues quickly with our comprehensive help resources.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          <QuickLink
            icon={<MessageCircle className="h-8 w-8 text-[#00ffff]" />}
            title="Live Chat Support"
            description="Chat with our support team in real-time"
          />
          <QuickLink
            icon={<Phone className="h-8 w-8 text-[#00ffff]" />}
            title="Phone Support"
            description="Call us for immediate assistance"
          />
          <QuickLink
            icon={<Mail className="h-8 w-8 text-[#00ffff]" />}
            title="Email Support"
            description="Send us your questions anytime"
          />
        </div>
      </div>

      {/* Popular Topics */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-bold text-white mb-8">Popular Topics</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <TopicSection
            title="Getting Started"
            items={[
              "Creating an account",
              "Platform overview",
              "Basic setup guide",
              "First-time configuration"
            ]}
          />
          <TopicSection
            title="Account & Billing"
            items={[
              "Managing your subscription",
              "Payment methods",
              "Billing history",
              "Plan upgrades"
            ]}
          />
          <TopicSection
            title="Technical Support"
            items={[
              "API integration",
              "Error troubleshooting",
              "Performance optimization",
              "Security settings"
            ]}
          />
          <TopicSection
            title="Common Issues"
            items={[
              "Connection problems",
              "Data sync issues",
              "Access control",
              "Feature limitations"
            ]}
          />
        </div>
      </div>

      {/* Contact Options */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="white-holographic-card p-12 rounded-2xl">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Need More Help?</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <ContactOption
              icon={<MessageCircle className="h-12 w-12 text-[#00ffff]" />}
              title="Live Chat"
              description="Chat with our support team"
              action="Start Chat"
            />
            <ContactOption
              icon={<Mail className="h-12 w-12 text-[#00ffff]" />}
              title="Email Support"
              description="Get help via email"
              action="Send Email"
            />
            <ContactOption
              icon={<Book className="h-12 w-12 text-[#00ffff]" />}
              title="Documentation"
              description="Browse our docs"
              action="View Docs"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickLink({ icon, title, description }) {
  return (
    <div className="white-holographic-card p-6 rounded-xl flex items-center">
      <div className="mr-4">{icon}</div>
      <div>
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
}

function TopicSection({ title, items }) {
  return (
    <div className="white-holographic-card p-6 rounded-xl">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">{title}</h3>
      <ul className="space-y-3">
        {items.map((item, index) => (
          <li key={index}>
            <a href="#" className="text-[#00ffff] hover:text-[#ff0000] transition-colors flex items-center">
              <HelpCircle className="h-4 w-4 mr-2" />
              {item}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ContactOption({ icon, title, description, action }) {
  return (
    <div className="text-center">
      <div className="flex justify-center mb-4">{icon}</div>
      <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-600 mb-4">{description}</p>
      <button className="px-6 py-2 bg-[#00ffff] text-white rounded-lg hover:bg-[#ff0000] transition-colors">
        {action}
      </button>
    </div>
  );
}

export default HelpCenter;